import 'components/discovery/carousel/carousel.js'
import { CSSResultGroup, html, LitElement, nothing, PropertyValues } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { HeadingSlotMixin } from 'components/shared/heading/heading-slot-mixin.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createUrl } from '@cloudsuite/mosaic-shared/scripts/base-url.js'
import { breakpoints } from '@cloudsuite/mosaic-shared/styles/breakpoints.js'
import { parsePdpProducts } from 'components/discovery/pdp/typing/pdp-product.js'
import { tileConfigConverter, toInternalProduct } from 'components/shared/base-product-tile/base-product-tile.js'
import type { SimpleLayoutCfg } from 'components/shared/base-product-tile/base-product-tile.js'
import type { PdpListProduct } from 'components/discovery/pdp/typing/pdp-product.js'
import type { InternalProduct } from 'components/discovery/product/tile-parts/tile-parts-typing.js'
import {
  PRODUCT_LIST_FEATURES,
  productAlternativesPath,
  productListPath,
  productRecommendationsPath,
  productRelatedPath,
  productSimilarPath,
} from 'components/discovery/pdp/shared/pdp-endpoint-paths.js'
import { PRODUCT_SET_API, ProductSetAPI, ProductSetGetRequest } from './product-set-api.js'
import { style } from './product-set.style.js'
import 'components/discovery/carousel/components/carousel-item.js'

const DEFAULT_TILE_CONFIG: SimpleLayoutCfg = {
  image: {},
  productInfo: {
    layout: [['zone-1']],
    zones: {
      'zone-1': {
        alignItems: 'center',
        widgets: [
          { type: 'csc-product-tile-title' },
          { type: 'csc-price' },
          { type: 'csc-featured-attributes' },
        ],
      },
    },
  },
  orderActions: {
    layout: [['zone-1']],
    zones: {
      'zone-1': { direction: 'row', justifyContent: 'flex-end', widgets: [{ type: 'csc-order-form' }] },
    },
  },
}

export const productSetDataSources = ['alternatives', 'related', 'similar', 'recent', 'recommended'] as const
export type ProductSetDataSource = typeof productSetDataSources[number]

export const productSetLayouts = ['carousel', 'col-three', 'col-two', 'list'] as const
export type ProductSetLayout = typeof productSetLayouts[number]

const colLayoutGridClass: Record<'col-three' | 'col-two', string> = {
  'col-three': 'grid',
  'col-two': 'grid-2',
}

const productPathByDataSource: Record<Exclude<ProductSetDataSource, 'recent'>, (productId: number) => string> = {
  alternatives: productAlternativesPath,
  related: productRelatedPath,
  similar: productSimilarPath,
  recommended: productRecommendationsPath,
}

const DEFAULT_ITEMS_PER_VIEW = [4, 2, 1.15] as const
const DEFAULT_DISPLAY_LIMIT = 100

const parseItemsPerView = (value: string | null): number[] => {
  const counts = (value ?? '').split(',').map(entry => parseFloat(entry.trim()))
  return DEFAULT_ITEMS_PER_VIEW.map((fallback, index) => {
    const count = counts[index]
    return count !== undefined && count > 0 ? count : fallback
  })
}

const isLegacyItemsPerView = (value: string): boolean =>
  value.includes(',') || !Number.isInteger(Number(value))

const parseDisplayLimit = (value: string | null): number | number[] => {
  if (value === null) return DEFAULT_DISPLAY_LIMIT
  return isLegacyItemsPerView(value) ? parseItemsPerView(value) : Number(value)
}

const skeletonCount: Record<ProductSetLayout, number> = {
  'col-three': 6,
  'col-two': 4,
  'carousel': 4,
  'list': 2,
}

@customElement('csc-product-set')
export class CSCProductSet extends HeadingSlotMixin(LitElement) {
  static override styles: CSSResultGroup = [hostStyle, style]

  @inject(PRODUCT_SET_API)
  private api!: ProductSetAPI

  @state()
  private isMobile = false

  private mediaQuery: MediaQueryList | undefined

  private onMediaChange = (e: MediaQueryListEvent) => {
    this.isMobile = e.matches
  }

  override connectedCallback() {
    super.connectedCallback()
    this.mediaQuery = window.matchMedia(`(max-width: ${breakpoints.medium - 1}px)`)
    this.isMobile = this.mediaQuery.matches
    this.mediaQuery.addEventListener('change', this.onMediaChange)
    void this.fetchProducts()
  }

  override disconnectedCallback() {
    super.disconnectedCallback()
    this.mediaQuery?.removeEventListener('change', this.onMediaChange)
  }

  @state()
  private products: InternalProduct[] = []

  @state()
  private loading = false

  @property({ type: String, attribute: 'data-source' })
  dataSource: ProductSetDataSource = 'related'

  @property({ type: String, attribute: 'layout' })
  layout: ProductSetLayout = 'carousel'

  @property({ type: Array, attribute: 'items-per-view', converter: { fromAttribute: parseItemsPerView } })
  itemsPerView: number[] = [...DEFAULT_ITEMS_PER_VIEW]

  @state()
  private limit = DEFAULT_DISPLAY_LIMIT

  @property({ attribute: 'display-limit', converter: { fromAttribute: parseDisplayLimit } })
  set displayLimit(value: number | number[]) {
    if (Array.isArray(value)) {
      console.warn('<csc-product-set> items per view in "display-limit" are deprecated, use "items-per-view" instead. "display-limit" is now the maximum number of products shown.')
      this.itemsPerView = value
      return
    }
    this.limit = value
  }

  get displayLimit(): number {
    return this.limit
  }

  /** @deprecated Use `display-limit` instead. */
  @property({ type: Number, attribute: 'max-items' })
  set maxItems(value: number) {
    console.warn('<csc-product-set> "max-items" is deprecated, use "display-limit" instead.')
    this.limit = value
  }

  get maxItems() {
    return this.limit
  }

  @property({ type: Object, attribute: 'tile-config', converter: tileConfigConverter('csc-product-set') })
  tileConfig: SimpleLayoutCfg | null = null

  private effectiveTileConfig: SimpleLayoutCfg = DEFAULT_TILE_CONFIG

  override willUpdate(changedProperties: PropertyValues) {
    super.willUpdate(changedProperties)
    if (changedProperties.has('tileConfig')) {
      this.effectiveTileConfig = {
        ...DEFAULT_TILE_CONFIG,
        ...this.tileConfig,
      }
    }
  }

  @property({ type: Number, attribute: 'product-id' })
  productId: number | null = null

  /** Comma-separated product ids. Only used when `data-source` is `recent`; every other data source uses `product-id`. */
  @property({ type: String, attribute: 'product-ids' })
  productIds = ''

  @property({ type: String })
  locale = 'en-US'

  private itemsPerViewAt(index: 0 | 1 | 2) {
    return this.itemsPerView[index] ?? DEFAULT_ITEMS_PER_VIEW[index]
  }

  private async resolveEndpoint(): Promise<string> {
    if (this.dataSource === 'recent') {
      const ids = this.productIds.split(',').map(id => id.trim()).filter(Boolean)
      if (!ids.length) return ''
      const url = await createUrl(productListPath())
      ids.forEach(id => url.searchParams.append('id', id))
      PRODUCT_LIST_FEATURES.forEach(feature => url.searchParams.append('features', feature))
      return url.href
    }

    if (this.productId === null) return ''
    return (await createUrl(productPathByDataSource[this.dataSource](this.productId))).href
  }

  private async fetchProducts() {
    const endpoint = await this.resolveEndpoint()
    if (!endpoint) return
    this.loading = true
    try {
      const result = await this.api.getProducts(new ProductSetGetRequest(endpoint))
      this.products = this.toTileProducts(parsePdpProducts(result))
    } catch (error) {
      this.products = []
      console.error('<csc-product-set> failed to load products', error)
    } finally {
      this.loading = false
    }
  }

  private get renderedProducts(): InternalProduct[] {
    return this.products.slice(0, this.displayLimit)
  }

  private toTileProducts(products: PdpListProduct[]): InternalProduct[] {
    return products.map(p => toInternalProduct({ ...p, variants: p.variants ?? [] }))
  }

  private renderSkeletons() {
    return Array.from({ length: skeletonCount[this.layout] }, (_, index) => html`
      <csc-base-product-tile
        loading
        index=${index}
      ></csc-base-product-tile>
    `)
  }

  private get tileCardVariant(): 'default' | 'wide' | 'inline' {
    if (this.layout !== 'list') return 'default'
    return this.isMobile ? 'inline' : 'wide'
  }

  private renderTile(product: InternalProduct) {
    return html`
      <csc-base-product-tile
        .product=${product}
        card-variant=${this.tileCardVariant}
        locale=${this.locale}
        .tileConfig=${this.effectiveTileConfig}
      ></csc-base-product-tile>
    `
  }

  private renderCarousel() {
    return html`
      <div class="col-layout">
        ${this.renderHeadingSlot()}
        <csc-carousel
          ?pagination=${true}
          controls-position="bottom"
          items-per-view-desktop=${this.itemsPerViewAt(0)}
          items-per-view-tablet=${this.itemsPerViewAt(1)}
          items-per-view-mobile=${this.itemsPerViewAt(2)}
          stretch-tiles
        >
          ${this.loading
            ? this.renderSkeletons().map(skeleton => html`<csc-carousel-item>${skeleton}</csc-carousel-item>`)
            : this.renderedProducts.map(product => html`<csc-carousel-item>${this.renderTile(product)}</csc-carousel-item>`)
          }
        </csc-carousel>
      </div>
    `
  }

  private renderColLayout() {
    return html`
      <div class="col-layout">
        ${this.renderHeadingSlot()}
        <div class=${colLayoutGridClass[this.layout as 'col-three' | 'col-two']}>
          ${this.loading ? this.renderSkeletons() : this.renderedProducts.map(product => this.renderTile(product))}
        </div>
      </div>
    `
  }

  private renderList() {
    return html`
      <div class="col-layout">
        ${this.renderHeadingSlot()}
        <div class="list">
          ${this.loading
            ? this.renderSkeletons()
            : this.renderedProducts.map(product => this.renderTile(product))
          }
        </div>
      </div>
    `
  }

  override render() {
    if (!this.loading && !this.renderedProducts.length) return nothing
    if (this.layout === 'list') return this.renderList()
    if (this.layout in colLayoutGridClass && !this.isMobile) return this.renderColLayout()
    return this.renderCarousel()
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-product-set': CSCProductSet
  }
}
